<template>
  <div class="contact-page">
    <Loading
      :active="isLoading"
      :color="'#c8a246'"
      :height="70"
      :width="70"
      backgroundColor="rgba(242,240,234,.9)"
      :is-full-page="true"
    />

    <!-- Hero -->
    <section class="contact-hero">
      <div class="contact-shell">
        <div class="contact-hero__badge">
          <span></span>
          <p>SHC Capital</p>
        </div>
        <h1 class="contact-hero__title">
          <i class="fas fa-handshake"></i>
          {{ $t('contact.contactUs') }}
        </h1>
        <p class="contact-hero__sub">{{ $t('contact.aboutUs') }}</p>
        <div class="contact-hero__badges">
          <div class="contact-hero__badge-item">
            <i class="fas fa-clock"></i>
            <span>{{ $t('contact.support') }}</span>
          </div>
          <div class="contact-hero__badge-item">
            <i class="fas fa-shield-alt"></i>
            <span>{{ $t('contact.security') }}</span>
          </div>
          <div class="contact-hero__badge-item">
            <i class="fas fa-award"></i>
            <span>{{ $t('contact.master') }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- Contact cards -->
    <section class="contact-methods">
      <div class="contact-shell">
        <div class="contact-methods__grid">

          <div class="contact-card">
            <div class="contact-card__icon contact-card__icon--blue">
              <i class="fas fa-phone-alt"></i>
            </div>
            <h3 class="contact-card__title">{{ $t('contact.hotline') }}</h3>
            <p class="contact-card__desc">{{ $t('contact.callme') }}</p>
            <a href="tel:0766726416" class="contact-card__value">{{ $t('tel') }}</a>
            <p class="contact-card__note">{{ $t('contact.freetel') }}</p>
          </div>

          <div class="contact-card">
            <div class="contact-card__icon contact-card__icon--green">
              <i class="fas fa-envelope"></i>
            </div>
            <h3 class="contact-card__title">{{ $t('common.email') }}</h3>
            <p class="contact-card__desc">{{ $t('contact.mailtome') }}</p>
            <p class="contact-card__value contact-card__value--email">info.songhancapital@gmail.com</p>
            <p class="contact-card__note">{{ $t('contact.feedback24') }}</p>
          </div>

          <div class="contact-card">
            <div class="contact-card__icon contact-card__icon--gold">
              <i class="fas fa-map-marker-alt"></i>
            </div>
            <h3 class="contact-card__title">{{ $t('contact.department') }}</h3>
            <p class="contact-card__desc">{{ $t('contact.departmentLive') }}</p>
            <address class="contact-card__address">
              <p>{{ $t('street') }}</p>
              <p>{{ $t('ward') }}</p>
              <p>{{ $t('city') }}</p>
            </address>
            <p class="contact-card__note">T2–T6: 8:00–17:00</p>
          </div>

        </div>
      </div>
    </section>

    <!-- Form + Map -->
    <section class="contact-main">
      <div class="contact-shell">
        <div class="contact-main__grid">

          <!-- Form -->
          <div class="contact-form-wrap">
            <h2 class="contact-section-title">
              <i class="fas fa-paper-plane"></i>
              {{ $t('contact.sendMessage') }}
            </h2>
            <div v-if="checkLanguage('vi')">
              <iframe
                src="https://songhancapital.getflycrm.com/api/forms/viewform?key=T1lZWsggxhLM7aWUmje9sEXiegHCvjHHbopQ8zosrQtNF1kCq6"
                width="100%" height="500px" frameborder="0" marginheight="0" marginwidth="0"
              >Loading...</iframe>
            </div>
            <div v-else>
              <iframe
                src="https://songhancapital.getflycrm.com/api/forms/viewform?key=y0ezkrSYLHadr3lMCCFnGlXS6HeV0Tg3jCxQmmD8w4fL6qBubh"
                width="100%" height="500px" frameborder="0" marginheight="0" marginwidth="0"
              >Loading...</iframe>
            </div>
          </div>

          <!-- Map + Info -->
          <div class="contact-side">

            <!-- Map -->
            <div class="contact-map">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3833.8078032532967!2d108.21746217486945!3d16.075460339271668!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31421830aaa63bc5%3A0xff1b117af3ee440a!2zMTE1IEzDqiBM4bujaSwgVGjhuqFjaCBUaGFuZywgSOG6o2kgQ2jDonUsIMSQw6AgTuG6tW5nIDU1MDAwMCwgVmlldG5hbQ!5e0!3m2!1sen!2s!4v1751219628059!5m2!1sen!2s"
                width="100%" height="100%" style="border:0" allowfullscreen="" loading="lazy"
                referrerpolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>

            <!-- Quick info -->
            <div class="contact-info-box">
              <h3 class="contact-section-title contact-section-title--sm">
                <i class="fas fa-info-circle"></i>
                {{ $t('contact.infoFast') }}
              </h3>
              <ul class="contact-info-list">
                <li>
                  <i class="fas fa-clock"></i>
                  <div>
                    <p>{{ $t('contact.workingTime') }}</p>
                    <span>{{ $t('contact.support') }}</span>
                  </div>
                </li>
                <li>
                  <i class="fas fa-headset"></i>
                  <div>
                    <p>{{ $t('contact.supportOnline') }}</p>
                    <span>{{ $t('contact.supportOnlineHotLine') }}</span>
                  </div>
                </li>
                <li>
                  <i class="fas fa-language"></i>
                  <div>
                    <p>{{ $t('contact.languageSupport') }}</p>
                    <span>{{ $t('contact.language') }}</span>
                  </div>
                </li>
              </ul>
            </div>

            <!-- Social -->
            <div class="contact-info-box">
              <h3 class="contact-section-title contact-section-title--sm">
                <i class="fas fa-share-alt"></i>
                {{ $t('contact.followMe') }}
              </h3>
              <div class="contact-socials">
                <a
                  v-for="social in socialLinks"
                  :key="social.name"
                  :href="social.url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="contact-social-btn"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path :d="social.icon" />
                  </svg>
                  <span>{{ social.name }}</span>
                </a>
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>

    <!-- FAQ -->
    <section class="contact-faq">
      <div class="contact-shell">
        <div class="contact-faq__header">
          <h2 class="contact-faq__title">{{ $t('contact.question') }}</h2>
          <p class="contact-faq__sub">{{ $t('contact.learnMore') }}</p>
        </div>

        <div class="contact-faq__list">
          <div
            v-for="(faq, index) in faqData"
            :key="index"
            :class="['contact-faq__item', faqStates[index] ? 'contact-faq__item--open' : '']"
          >
            <button class="contact-faq__question" @click="toggleFAQ(index)">
              <span>{{ faq.question }}</span>
              <i :class="['fas fa-chevron-down contact-faq__chevron', faqStates[index] ? 'contact-faq__chevron--open' : '']"></i>
            </button>
            <div v-show="faqStates[index]" class="contact-faq__answer">
              <p>{{ faq.answer }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

  </div>
</template>

<script src="./contact.js"></script>
<style src="./contact.scss" lang="scss"></style>
